import React, { useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import "./Integrate.css";

const GET_MY_SITE = gql`query { mySite { id name installToken } }`;
const GET_CONNECTORS = gql`query { siteConnectors { id connector label monthlyPrice status mapping } }`;
const ACTIVATE = gql`mutation($connector: String!, $label: String, $monthlyPrice: Int) { activateConnector(connector: $connector, label: $label, monthlyPrice: $monthlyPrice) { id } }`;
const DEACTIVATE = gql`mutation($id: ID!) { deactivateConnector(id: $id) }`;
const SET_MAPPING = gql`mutation($id: ID!, $mapping: JSON) { setConnectorMapping(id: $id, mapping: $mapping) { id } }`;
const GET_CATALOG = gql`query { connectorCatalog { id key name description monthlyPrice premium published } }`;

const DD_FIELDS = ["droneId", "lat", "lng", "altitudeM", "speedMs", "directionDeg", "model", "sensorType", "distanceM", "signalDb"];

export default function Integrate() {
  const { data } = useQuery(GET_MY_SITE);
  const connQ = useQuery(GET_CONNECTORS);
  const catQ = useQuery(GET_CATALOG);
  const catalog = catQ.data?.connectorCatalog || [];
  const [activate] = useMutation(ACTIVATE, { onCompleted: () => connQ.refetch() });
  const [deactivate] = useMutation(DEACTIVATE, { onCompleted: () => connQ.refetch() });
  const [setMapping] = useMutation(SET_MAPPING, { onCompleted: () => connQ.refetch() });
  const [mapConnId, setMapConnId] = useState("");
  const [mapDraft, setMapDraft] = useState<Record<string, string>>({});
  const pickConn = (id: string) => {
    setMapConnId(id);
    const c = (connQ.data?.siteConnectors || []).find((x: any) => x.id === id);
    setMapDraft((c && c.mapping) ? { ...c.mapping } : {});
  };
  const saveMapping = () => {
    const clean: Record<string, string> = {};
    Object.entries(mapDraft).forEach(([k, v]) => { if (v) clean[k] = v; });
    setMapping({ variables: { id: mapConnId, mapping: clean } });
  };

  const site = data?.mySite;
  const origin = window.location.origin;
  const token = site?.installToken || "<your-site-token>";

  const [sbInput, setSbInput] = useState('{"droneId":"SANDBOX-1","lat":48.31,"lng":18.07,"altitudeM":100,"speedMs":8,"model":"Test Sensor","sensorType":"CRPC"}');
  const [sbResult, setSbResult] = useState("");
  const testPacket = async () => {
    try {
      const body = JSON.parse(sbInput);
      body.token = site?.installToken; body.dryRun = true;
      const r = await fetch(`${origin}/api/ingest`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      setSbResult(JSON.stringify(await r.json(), null, 2));
    } catch (e: any) { setSbResult("Error: " + e.message); }
  };

  const active: Record<string, any> = {};
  (connQ.data?.siteConnectors || []).forEach((c: any) => { active[c.connector] = c; });
  const monthlyTotal = (connQ.data?.siteConnectors || []).reduce((s: number, c: any) => s + (c.monthlyPrice || 0), 0);
  const activeConns = (connQ.data?.siteConnectors || []).filter((c: any) => c.status === "ACTIVE");

  const tier = (c: any) => c.key === "CUSTOM" ? "from $1,000" : c.monthlyPrice === 0 ? "Free" : "+$" + c.monthlyPrice + "/mo";

  const curlEx =
`curl -X POST ${origin}/api/ingest \\
  -H "Content-Type: application/json" \\
  -d '{"token":"${token}","droneId":"TEST-1","lat":48.31,"lng":18.07,"altitudeM":120,"speedMs":12,"model":"DJI Mavic","sensorType":"CRPC"}'`;

  const psEx =
`Invoke-RestMethod -Uri "${origin}/api/ingest" -Method Post -ContentType "application/json" -Body '{"token":"${token}","droneId":"TEST-1","lat":48.31,"lng":18.07,"altitudeM":120,"speedMs":12,"model":"DJI Mavic","sensorType":"CRPC"}'`;

  // DD Camera Connector — ONVIF/RTSP → /api/ingest (устанавливается в сети объекта)
  const camPs = `irm "${origin}/install-camera-connector.ps1?token=${token}" | iex`;
  const camSh = `curl -fsSL "${origin}/install-camera-connector.sh?token=${token}" | bash`;
  const camCfgUrl = `${origin}/connector/config?token=${token}`;

  return (
    <div className="page-container">
      <div className="page-header"><h2>🔌 Integration Hub — DD-Connect</h2></div>

      <div className="int-card">
        <h3>Unified ingest API{site ? ` — ${site.name}` : ""}</h3>
        {!site && <p className="int-note">Sign in as a site operator to see this object's ingest token.</p>}
        <div className="int-row"><span>Endpoint</span><code>POST {origin}/api/ingest</code></div>
        <div className="int-row"><span>Site token</span><code className="int-token">{token}</code></div>
        <p className="int-desc">
          Any third-party device or driver can push detections here. Fields:
          <b> droneId</b> (required), lat, lng, altitudeM, speedMs, directionDeg, model, sensorType, distanceM, signalDb.
          Targets appear on this object's map and threat list in real time, isolated to your object.
        </p>
        <div className="int-tabs">curl (Linux / WSL)</div>
        <pre className="int-code">{curlEx}</pre>
        <div className="int-tabs">PowerShell (Windows)</div>
        <pre className="int-code">{psEx}</pre>
      </div>

      <div className="int-card">
        <h3>📷 Camera Connector — ONVIF / RTSP</h3>
        <p className="int-desc">
          Universal camera connector for this object. Runs in the client's network (behind NAT — outbound HTTPS only),
          connects ONVIF/RTSP cameras and pushes detections to the unified API above. Detector is switchable
          (<b>passthrough</b> / <b>motion</b> / <b>onnx-yolo</b>) in <code>config.json</code>. Needs Docker.
        </p>
        <div className="int-tabs">Windows (PowerShell, Docker Desktop)</div>
        <pre className="int-code">{camPs}</pre>
        <div className="int-tabs">Linux (Docker)</div>
        <pre className="int-code">{camSh}</pre>
        <div className="int-row" style={{ marginTop: 10 }}>
          <span>Site config.json</span>
          <a className="int-btn" href={camCfgUrl} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>Download config.json</a>
        </div>
        <p className="int-desc">After install, edit <code>config.json</code> → add cameras (ONVIF host or rtspUrl), then <code>docker restart dd-camera-connector</code>. Local status: <code>http://localhost:3001</code>.</p>
      </div>

      <div className="int-card">
        <h3>Sandbox — validate a packet</h3>
        <p className="int-desc">Paste a raw detection packet. It is validated against the unified DD-API and normalized — without writing to the live feed.</p>
        <textarea className="int-sb" rows={4} value={sbInput} onChange={e => setSbInput(e.target.value)} />
        <div><button className="int-btn" disabled={!site} onClick={testPacket}>Test packet</button></div>
        {sbResult && <pre className="int-code" style={{ marginTop: 10 }}>{sbResult}</pre>}
      </div>

      <div className="int-cathead">
        <h3 className="int-h">Connector catalog</h3>
        <span className="int-total">Monthly add-on: <b>${monthlyTotal}</b>/mo</span>
      </div>

      <div className="int-grid">
        {catalog.map((c: any) => {
          const on = active[c.key];
          const custom = c.key === "CUSTOM";
          return (
            <div key={c.key} className={"int-conn" + (on ? " int-on" : "")}>
              <div className="int-conn-head">
                <span className="int-conn-name">{c.name}</span>
                <span className={"int-tier" + (c.monthlyPrice === 0 && !custom ? " free" : "")}>{tier(c)}</span>
              </div>
              <div className="int-conn-desc">{c.description}</div>
              {custom ? (
                <div className="int-conn-status s-onrequest">On request</div>
              ) : on ? (
                <div className="int-conn-actions">
                  <span className="int-conn-status s-active">● Active</span>
                  <button className="int-btn off" disabled={!site} onClick={() => deactivate({ variables: { id: on.id } })}>Deactivate</button>
                </div>
              ) : (
                <button className="int-btn" disabled={!site} onClick={() => activate({ variables: { connector: c.key, label: c.name, monthlyPrice: c.monthlyPrice } })}>
                  Activate{c.monthlyPrice ? ` (+$${c.monthlyPrice}/mo)` : ""}
                </button>
              )}
            </div>
          );
        })}
      </div>
      {!site && <p className="int-note" style={{ marginTop: 12 }}>Connector activation is per-object — sign in as a site operator to enable.</p>}

      {activeConns.length > 0 && (
        <div className="int-card" style={{ marginTop: 20 }}>
          <h3>Field mapping</h3>
          <p className="int-desc">
            Map an external device's field names to DroneDefender fields. Add <b>"connector":"KEY"</b> to your
            packet (KEY = connector code, e.g. RADAR) — the API normalizes it on ingest. Leave a row blank to skip.
          </p>
          <div className="int-row">
            <span>Connector</span>
            <select value={mapConnId} onChange={e => pickConn(e.target.value)}>
              <option value="">— active connector —</option>
              {activeConns.map((c: any) => <option key={c.id} value={c.id}>{(c.label || c.connector)} ({c.connector})</option>)}
            </select>
          </div>
          {mapConnId && (
            <>
              <div className="int-maps">
                {DD_FIELDS.map(f => (
                  <div key={f} className="int-maprow">
                    <span className="int-ddf">{f}</span>
                    <span className="int-arrow">←</span>
                    <input placeholder="source field" value={mapDraft[f] || ""} onChange={e => setMapDraft(p => ({ ...p, [f]: e.target.value }))} />
                  </div>
                ))}
              </div>
              <button className="int-btn" onClick={saveMapping}>Save mapping</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
